import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Briefcase,
  Building2,
  Clock,
  Users,
  Link2,
  FileText,
  Search,
  X,
  ChevronLeft,
  Save,
  Send,
  ShieldCheck,
  CalendarDays,
  Plus,
  Trash2,
  Loader2,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function CareerPicker({ selected, onChange, allCareers, userName, pendingCareers, onPendingChange }) {
  const [search, setSearch] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ career_name: '', category: '', short_description: '' });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allCareers;
    return allCareers.filter(
      (c) => c.name.toLowerCase().includes(q) || c.category.toLowerCase().includes(q)
    );
  }, [search, allCareers]);

  const toggle = (slug) => {
    if (selected.includes(slug)) {
      onChange(selected.filter((s) => s !== slug));
    } else {
      onChange([...selected, slug]);
    }
  };

  const removePending = (name) => {
    onPendingChange((pendingCareers || []).filter(p => p.name !== name));
  };

  const handleRequestSubmit = async () => {
    if (!requestForm.career_name.trim()) {
      toast.error('Please enter a career name');
      return;
    }
    setRequestSubmitting(true);
    try {
      await api.post('/career-requests', {
        requested_career_name: requestForm.career_name,
        category: requestForm.category || null,
        short_description: requestForm.short_description || null,
        source: 'mentor',
        requested_by_name: userName || '',
      });
      // Add as pending career mapping
      const newPending = {
        name: requestForm.career_name.trim(),
        category: requestForm.category?.trim() || '',
        status: 'pending_review',
      };
      onPendingChange([...(pendingCareers || []), newPending]);
      setRequestSent(true);
    } catch {
      toast.error('Failed to submit request');
    }
    setRequestSubmitting(false);
  };

  const selectedCareers = allCareers.filter((c) => selected.includes(c.slug));

  return (
    <div data-testid="career-picker">
      {/* Selected careers + pending */}
      {(selectedCareers.length > 0 || (pendingCareers || []).length > 0) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedCareers.map((c) => (
            <Badge
              key={c.slug}
              className="bg-[#FFF0ED] text-[#F97D6A] border-0 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 cursor-pointer hover:bg-[#FFE4DE] transition-colors"
              onClick={() => toggle(c.slug)}
              data-testid={`selected-career-${c.slug}`}
            >
              {c.name}
              <X className="h-3 w-3" />
            </Badge>
          ))}
          {(pendingCareers || []).map((p) => (
            <Badge
              key={`pending-${p.name}`}
              className="bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => removePending(p.name)}
              data-testid={`pending-career-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {p.name}
              <span className="text-[9px] text-amber-500">(Pending Review)</span>
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search careers to map..."
          className="pl-10 rounded-xl h-11 border-gray-200"
          data-testid="career-search-input"
        />
      </div>
      <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
        {filtered.length === 0 && search.trim() ? (
          <div className="p-3 text-center">
            <p className="text-xs text-[#9CA3AF] mb-2">No careers found for "{search}"</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setRequestSent(false); setRequestForm({ career_name: search, category: '', short_description: '' }); setShowRequestModal(true); }}
              className="text-xs rounded-lg h-7 px-3 text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="career-not-listed-search-btn"
            >
              <HelpCircle className="h-3 w-3 mr-1" /> Request "{search}" as a new career
            </Button>
          </div>
        ) : (
          <>
            {filtered.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => toggle(c.slug)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                  selected.includes(c.slug)
                    ? 'bg-[#FFF0ED] text-[#F97D6A]'
                    : 'hover:bg-gray-50 text-[#4B5563]'
                }`}
                data-testid={`career-option-${c.slug}`}
              >
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="text-[#9CA3AF] text-xs ml-2">{c.category}</span>
                </span>
                {selected.includes(c.slug) && (
                  <span className="text-[10px] font-bold text-[#F97D6A]">SELECTED</span>
                )}
              </button>
            ))}
            {/* "Not listed" option */}
            <button
              type="button"
              onClick={() => { setRequestSent(false); setRequestForm({ career_name: '', category: '', short_description: '' }); setShowRequestModal(true); }}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-[#6B7280] hover:bg-amber-50 hover:text-amber-700 transition-colors"
              data-testid="career-not-listed-btn"
            >
              <HelpCircle className="h-3.5 w-3.5 shrink-0" />
              <span className="font-medium">My career is not listed</span>
            </button>
          </>
        )}
      </div>

      {/* Request New Career Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="request-career-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-[#1F2937]">Request a New Career</DialogTitle>
            <DialogDescription>We'll review and add this career to the platform.</DialogDescription>
          </DialogHeader>
          {requestSent ? (
            <div className="text-center py-4" data-testid="request-career-success">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-[#1F2937] mb-1">Career requested!</p>
              <p className="text-xs text-[#6B7280]">Added to your application as pending review. You can continue your submission.</p>
              <Button onClick={() => setShowRequestModal(false)} className="mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm">Continue</Button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div>
                  <Label className="text-[#1F2937] font-medium text-sm">Career Name *</Label>
                  <Input value={requestForm.career_name} onChange={e => setRequestForm({...requestForm, career_name: e.target.value})} placeholder="e.g. Marine Biologist" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="request-career-name" />
                </div>
                <div>
                  <Label className="text-[#1F2937] font-medium text-sm">Category (optional)</Label>
                  <Input value={requestForm.category} onChange={e => setRequestForm({...requestForm, category: e.target.value})} placeholder="e.g. Science & Healthcare" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="request-career-category" />
                </div>
                <div>
                  <Label className="text-[#1F2937] font-medium text-sm">Short Description (optional)</Label>
                  <Textarea value={requestForm.short_description} onChange={e => setRequestForm({...requestForm, short_description: e.target.value})} placeholder="Brief description of this career..." className="mt-1 rounded-xl border-gray-200 text-sm min-h-[60px] resize-none" data-testid="request-career-desc" />
                </div>
              </div>
              <DialogFooter className="gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)} className="rounded-xl text-sm">Cancel</Button>
                <Button onClick={handleRequestSubmit} disabled={requestSubmitting} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm" data-testid="request-career-submit">{requestSubmitting ? 'Submitting...' : 'Submit Request'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SESSION_DURATIONS = [90, 120];

const spanOf = (tr) => {
  if (!tr?.start || !tr?.end) return null;
  const [sh, sm] = tr.start.split(':').map(Number);
  const [eh, em] = tr.end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
};

const addMinutes = (time, minutes) => {
  const [h, m] = (time || '09:00').split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const newSlot = (start = '09:00', duration = 90) => ({
  start, end: addMinutes(start, duration), duration_minutes: duration,
});

function AvailabilityEditor({ config, onChange }) {
  const addDay = (day) => {
    if (config.find((d) => d.day === day)) return;
    onChange([...config, { day, time_ranges: [newSlot()] }]);
  };

  const removeDay = (day) => {
    onChange(config.filter((d) => d.day !== day));
  };

  const addTimeRange = (day) => {
    onChange(
      config.map((d) =>
        d.day === day
          ? { ...d, time_ranges: [...d.time_ranges, newSlot()] }
          : d
      )
    );
  };

  const removeTimeRange = (day, idx) => {
    onChange(
      config.map((d) =>
        d.day === day
          ? { ...d, time_ranges: d.time_ranges.filter((_, i) => i !== idx) }
          : d
      )
    );
  };

  const updateSlot = (day, idx, patch) => {
    onChange(
      config.map((d) =>
        d.day === day
          ? {
              ...d,
              time_ranges: d.time_ranges.map((tr, i) => {
                if (i !== idx) return tr;
                const start = patch.start ?? tr.start;
                const duration = patch.duration_minutes ?? tr.duration_minutes ?? 90;
                return { start, duration_minutes: duration, end: addMinutes(start, duration) };
              }),
            }
          : d
      )
    );
  };

  const activeDays = config.map((d) => d.day);

  return (
    <div data-testid="availability-editor">
      <div className="flex flex-wrap gap-2 mb-4">
        {WEEKDAYS.map((day) => (
          <button
            key={day}
            type="button"
            onClick={() => (activeDays.includes(day) ? removeDay(day) : addDay(day))}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeDays.includes(day)
                ? 'bg-[#F97D6A] text-white'
                : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
            }`}
            data-testid={`day-toggle-${day.toLowerCase()}`}
          >
            {day.slice(0, 3)}
          </button>
        ))}
      </div>

      {config.length === 0 && (
        <p className="text-xs text-[#9CA3AF] text-center py-4">
          Select days above to set your availability
        </p>
      )}

      <p className="text-xs text-[#6B7280] mb-3">
        Each slot is one mentoring session: 90 minutes as standard, or 120 minutes if you prefer
        an extended session. Add a second slot for another session on the same day.
      </p>

      <div className="space-y-3">
        {WEEKDAYS.filter((day) => activeDays.includes(day)).map((day) => {
          const dayConfig = config.find((d) => d.day === day);
          return (
            <div
              key={day}
              className="bg-gray-50 rounded-xl p-3"
              data-testid={`availability-day-${day.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#1F2937]">{day}</span>
                <button
                  type="button"
                  onClick={() => addTimeRange(day)}
                  className="text-xs text-[#F97D6A] hover:underline flex items-center gap-1"
                  data-testid={`add-slot-${day.toLowerCase()}`}
                >
                  <Plus className="h-3 w-3" /> Add slot
                </button>
              </div>
              {dayConfig.time_ranges.map((tr, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2 flex-wrap">
                  <Input
                    type="time"
                    value={tr.start}
                    onChange={(e) => updateSlot(day, idx, { start: e.target.value })}
                    className="h-9 rounded-lg border-gray-200 text-sm w-28"
                    data-testid={`time-start-${day.toLowerCase()}-${idx}`}
                  />
                  <select
                    value={tr.duration_minutes || (SESSION_DURATIONS.includes(spanOf(tr)) ? spanOf(tr) : '')}
                    onChange={(e) => updateSlot(day, idx, { duration_minutes: Number(e.target.value) })}
                    className="h-9 rounded-lg border border-gray-200 text-sm px-2 bg-white text-[#1F2937]"
                    data-testid={`duration-${day.toLowerCase()}-${idx}`}
                  >
                    {!SESSION_DURATIONS.includes(tr.duration_minutes || spanOf(tr)) && (
                      <option value="">Choose length</option>
                    )}
                    {SESSION_DURATIONS.map((d) => (
                      <option key={d} value={d}>{d} min{d === 90 ? ' (standard)' : ' (extended)'}</option>
                    ))}
                  </select>
                  <span className="text-xs text-[#9CA3AF]" data-testid={`slot-end-${day.toLowerCase()}-${idx}`}>
                    ends {tr.end || '--:--'}
                  </span>
                  {dayConfig.time_ranges.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTimeRange(day, idx)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-slot-${day.toLowerCase()}-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MentorOnboardingPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  const [allCareers, setAllCareers] = useState([]);

  const [form, setForm] = useState({
    profession_title: '',
    company_name: '',
    years_of_experience: '',
    linkedin_url: '',
    bio: '',
    notes_for_coordinator: '',
    mapped_career_ids: [],
    session_modes: ['online'],
    max_students_per_session: '5',
    availability_config: [],
    safety_agreement: false,
  });
  const [pendingCareers, setPendingCareers] = useState([]);
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, careersRes] = await Promise.all([
        api.get('/mentor-profile'),
        api.get('/careers/options'),
      ]);

      setAllCareers(careersRes.data);

      if (profileRes.data.exists) {
        setExistingProfile(profileRes.data);
        setForm({
          profession_title: profileRes.data.profession_title || '',
          company_name: profileRes.data.company_name || '',
          years_of_experience: String(profileRes.data.years_of_experience || ''),
          linkedin_url: profileRes.data.linkedin_url || '',
          bio: profileRes.data.bio || '',
          notes_for_coordinator: profileRes.data.notes_for_coordinator || '',
          mapped_career_ids: profileRes.data.mapped_career_ids || [],
          session_modes: profileRes.data.session_modes || ['online'],
          max_students_per_session: String(profileRes.data.max_students_per_session || 5),
          availability_config: profileRes.data.availability_config || [],
          safety_agreement: profileRes.data.safety_agreement || false,
        });
        setPendingCareers(profileRes.data.pending_career_mappings || []);
      }
    } catch {
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validate = (isDraft) => {
    const errs = {};
    if (!isDraft) {
      if (!form.profession_title.trim()) errs.profession_title = 'Required';
      if (!form.company_name.trim()) errs.company_name = 'Required';
      if (!form.years_of_experience || isNaN(form.years_of_experience) || Number(form.years_of_experience) < 0)
        errs.years_of_experience = 'Enter a valid number';
      if (!form.bio.trim()) errs.bio = 'Required';
      if (form.mapped_career_ids.length === 0 && pendingCareers.length === 0) errs.mapped_career_ids = 'Select at least one career';
      if (!form.safety_agreement) errs.safety_agreement = 'You must agree to continue';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (isDraft) => {
    if (!isApprovedEdit && !validate(isDraft)) {
      toast.error('Please fix the errors before continuing');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        profession_title: form.profession_title,
        company_name: form.company_name,
        years_of_experience: Number(form.years_of_experience) || 0,
        bio: form.bio,
        mapped_career_ids: form.mapped_career_ids,
        session_modes: ['online'],
        max_students_per_session: Number(form.max_students_per_session) || 5,
        availability_config: form.availability_config,
        preferred_city: null,
        linkedin_url: form.linkedin_url || null,
        notes_for_coordinator: form.notes_for_coordinator || null,
        safety_agreement: isApprovedEdit ? undefined : form.safety_agreement,
        pending_career_mappings: pendingCareers,
        save_as_draft: isApprovedEdit ? false : isDraft,
      };

      if (existingProfile) {
        const resp = await api.put('/mentor-profile', payload);
        if (isApprovedEdit && resp.data?.has_pending_changes) {
          toast.success('Professional changes submitted for coordinator review. Availability and other updates applied immediately.');
        } else {
          toast.success(isApprovedEdit ? 'Profile updated successfully' : isDraft ? 'Draft saved successfully' : 'Application submitted for review');
        }
      } else {
        await api.post('/mentor-profile', payload);
        toast.success(isDraft ? 'Draft saved successfully' : 'Application submitted for review');
      }

      await checkAuth?.();
      navigate('/mentor');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const isApprovedEdit = existingProfile?.approval_status === 'approved';
  const isEditable =
    !existingProfile ||
    ['draft', 'rejected', 'pending', 'approved'].includes(existingProfile.approval_status);

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center" data-testid="onboarding-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#F97D6A]" />
      </div>
    );
  }

  if (existingProfile && existingProfile.approval_status === 'approved') {
    // Allow editing — fall through to the form below with approved edit mode
  } else if (existingProfile && existingProfile.approval_status === 'suspended') {
    return (
      <div className="py-6 sm:py-8" data-testid="onboarding-suspended">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-8 w-8 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit'] mb-2">Mentor Profile Suspended</h2>
          <p className="text-sm text-[#6B7280] mb-2">Your mentor approval has been suspended by your Career Coordinator.</p>
          <p className="text-xs text-[#9CA3AF] mb-6">If you have questions, please contact your Career Coordinator.</p>
          <Button onClick={() => navigate('/parent')} variant="outline" className="rounded-xl">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 sm:py-8" data-testid="mentor-onboarding-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(isApprovedEdit ? '/mentor' : '/parent')}
            className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#1F2937] mb-4 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit']">
            {isApprovedEdit ? 'Edit Mentor Profile' : existingProfile ? 'Edit Mentor Profile' : 'Become a Career Mentor'}
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {isApprovedEdit ? 'Update your professional information. Your approval status will not change.' : 'Share your professional experience and help students at your school explore real careers.'}
          </p>
          {isApprovedEdit && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-xl px-4 py-3" data-testid="approved-edit-notice">
              Changes to profession, company, experience, bio or career mappings will be submitted for coordinator review. Availability, LinkedIn and notes update immediately.
            </div>
          )}
          {existingProfile?.approval_status === 'rejected' && (
            <div
              className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3"
              data-testid="rejected-notice"
            >
              Your previous application was not approved. Please review and update your profile, then resubmit.
            </div>
          )}
          {existingProfile?.approval_status === 'pending' && (
            <div
              className="mt-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3"
              data-testid="pending-notice"
            >
              Your application is under review. You can still edit and resubmit if needed.
            </div>
          )}
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Professional Info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-[#FFF0ED] flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-[#F97D6A]" />
              </div>
              <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">
                Professional Information
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1F2937] font-medium text-sm">
                  Your Profession / Job Title <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1.5">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    value={form.profession_title}
                    onChange={(e) => setForm({ ...form, profession_title: e.target.value })}
                    placeholder="e.g. Software Architect"
                    className={`pl-10 rounded-xl h-11 border-gray-200 ${errors.profession_title ? 'border-red-300' : ''}`}
                    disabled={!isEditable}
                    data-testid="input-profession-title"
                  />
                </div>
                {errors.profession_title && (
                  <p className="text-xs text-red-500 mt-1">{errors.profession_title}</p>
                )}
              </div>

              <div>
                <Label className="text-[#1F2937] font-medium text-sm">
                  Company / Workplace <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1.5">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="e.g. Google"
                    className={`pl-10 rounded-xl h-11 border-gray-200 ${errors.company_name ? 'border-red-300' : ''}`}
                    disabled={!isEditable}
                    data-testid="input-company-name"
                  />
                </div>
                {errors.company_name && (
                  <p className="text-xs text-red-500 mt-1">{errors.company_name}</p>
                )}
              </div>

              <div>
                <Label className="text-[#1F2937] font-medium text-sm">
                  Years of Experience <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1.5">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={form.years_of_experience}
                    onChange={(e) => setForm({ ...form, years_of_experience: e.target.value })}
                    placeholder="e.g. 10"
                    className={`pl-10 rounded-xl h-11 border-gray-200 ${errors.years_of_experience ? 'border-red-300' : ''}`}
                    disabled={!isEditable}
                    data-testid="input-years-experience"
                  />
                </div>
                {errors.years_of_experience && (
                  <p className="text-xs text-red-500 mt-1">{errors.years_of_experience}</p>
                )}
              </div>

              <div>
                <Label className="text-[#1F2937] font-medium text-sm">LinkedIn URL</Label>
                <div className="relative mt-1.5">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    value={form.linkedin_url}
                    onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                    className="pl-10 rounded-xl h-11 border-gray-200"
                    disabled={!isEditable}
                    data-testid="input-linkedin-url"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <Label className="text-[#1F2937] font-medium text-sm">
                Short Bio <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Tell us about your professional background and why you want to mentor students..."
                className={`mt-1.5 rounded-xl border-gray-200 min-h-[100px] resize-none ${errors.bio ? 'border-red-300' : ''}`}
                disabled={!isEditable}
                data-testid="input-bio"
              />
              {errors.bio && <p className="text-xs text-red-500 mt-1">{errors.bio}</p>}
            </div>

            <div className="mt-4">
              <Label className="text-[#1F2937] font-medium text-sm">Additional Notes</Label>
              <Textarea
                value={form.notes_for_coordinator}
                onChange={(e) => setForm({ ...form, notes_for_coordinator: e.target.value })}
                placeholder="Any additional information for the Career Coordinator..."
                className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none"
                disabled={!isEditable}
                data-testid="input-notes-coordinator"
              />
            </div>

            {/* Online-only info */}
            <div className="mt-4 bg-blue-50 rounded-xl p-3 flex items-start gap-2.5">
              <Users className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-800">Online Sessions</p>
                <p className="text-[11px] text-blue-600 mt-0.5">You'll be onboarded for online sessions. Each session runs 90 minutes, or 120 if you choose an extended slot. You can add workplace visits later from your mentor dashboard.</p>
              </div>
            </div>
          </div>

          {/* Career Mapping */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-[#FFF0ED] flex items-center justify-center">
                <FileText className="h-4 w-4 text-[#F97D6A]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">
                  Career Mapping <span className="text-red-500">*</span>
                </h2>
                <p className="text-xs text-[#9CA3AF]">Map your profession to relevant careers students can explore</p>
              </div>
            </div>
            {errors.mapped_career_ids && (
              <p className="text-xs text-red-500 mb-3">{errors.mapped_career_ids}</p>
            )}
            <CareerPicker
              selected={form.mapped_career_ids}
              onChange={(ids) => setForm({ ...form, mapped_career_ids: ids })}
              allCareers={allCareers}
              userName={user?.full_name}
              pendingCareers={pendingCareers}
              onPendingChange={setPendingCareers}
            />
          </div>

          {/* Availability */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-[#FFF0ED] flex items-center justify-center">
                <CalendarDays className="h-4 w-4 text-[#F97D6A]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">
                  Weekly Availability
                </h2>
                <p className="text-xs text-[#9CA3AF]">Set your typical weekly schedule for mentoring</p>
              </div>
            </div>
            <AvailabilityEditor
              config={form.availability_config}
              onChange={(config) => setForm({ ...form, availability_config: config })}
            />
          </div>

          {/* Mentor Safeguarding Declaration - only for new/pending applications */}
          {!isApprovedEdit && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-[#FFF0ED] flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-[#F97D6A]" />
              </div>
              <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">
                Mentor Safeguarding Declaration
              </h2>
            </div>
            <div className="bg-[#F5F6F8] rounded-xl p-4 mb-4 text-sm text-[#4B5563] leading-relaxed space-y-3">
              <p>As a CareersPOV Career Mentor, I understand that I will be interacting with school students as part of career exploration experiences.</p>
              <p className="font-medium text-[#1F2937]">I agree to:</p>
              <ul className="space-y-1.5 ml-1">
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Follow my school's safeguarding policies and the CareersPOV <a href="/student-safety" target="_blank" rel="noreferrer" className="text-[#F97D6A] hover:underline font-medium">Student Safety & Experience Guidelines</a>.</span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Maintain professional, respectful and age-appropriate conduct during all interactions.</span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Conduct mentoring activities only through school-approved arrangements and avoid unauthorized private communication with students.</span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Respect student privacy and maintain confidentiality of student information.</span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Promptly report any student safety or safeguarding concerns through the school's designated reporting channels.</span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>Cooperate with reasonable school oversight and comply with instructions concerning student safety.</span></li>
              </ul>
              <p className="text-xs text-[#9CA3AF] pt-1">I understand that my school may suspend or withdraw my approval to mentor if safeguarding requirements are not met.</p>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="safety-agreement"
                checked={form.safety_agreement}
                onCheckedChange={(checked) => setForm({ ...form, safety_agreement: !!checked })}
                disabled={!isEditable}
                data-testid="safety-agreement-checkbox"
                className="mt-0.5"
              />
              <label
                htmlFor="safety-agreement"
                className={`text-sm cursor-pointer ${errors.safety_agreement ? 'text-red-500' : 'text-[#4B5563]'}`}
              >
                I have read and agree to the Mentor Safeguarding Declaration and <a href="/student-safety" target="_blank" rel="noreferrer" className="text-[#F97D6A] hover:underline">Student Safety & Experience Guidelines</a>. <span className="text-red-500">*</span>
              </label>
            </div>
            {errors.safety_agreement && (
              <p className="text-xs text-red-500 mt-2">{errors.safety_agreement}</p>
            )}
          </div>
          )}

          {/* Actions */}
          {isApprovedEdit ? (
            <div className="flex flex-col sm:flex-row gap-3 pb-10">
              <Button
                variant="outline"
                onClick={() => navigate('/mentor')}
                className="rounded-xl h-11 px-6 border-gray-200"
                data-testid="cancel-edit-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 px-6 font-semibold"
                data-testid="save-profile-changes-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          ) : isEditable && (
            <div className="flex flex-col sm:flex-row gap-3 pb-10">
              <Button
                onClick={() => handleSave(true)}
                variant="outline"
                disabled={saving}
                className="rounded-xl h-11 px-6 border-gray-200"
                data-testid="save-draft-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 px-6 font-semibold"
                data-testid="submit-application-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {existingProfile ? 'Resubmit Application' : 'Submit Application'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
